import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaDownload, FaFileAlt, FaSyncAlt, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api';
import useApi from '../../hooks/useApi';
import Alert from '../common/Alert';

const statusDetails = {
  PENDING: { className: 'bg-warning text-dark', label: 'Awaiting verification' },
  VERIFIED: { className: 'bg-success', label: 'Verified' },
  REJECTED: { className: 'bg-danger', label: 'Changes requested' },
};

function VehicleDocuments({ vehicle, onClose }) {
  const { get, post, put, delete: deleteDocument } = useApi();
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const uploadRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceId, setReplaceId] = useState(null);

  const load = useCallback(async () => {
    try {
      setError('');
      setDocuments(await get(`/api/vehicle-documents?vehicleId=${vehicle.id}`));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [get, vehicle.id]);

  useEffect(() => {
    load();
  }, [load]);

  const sendFile = async (file, documentId) => {
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    setBusy(true);
    setError('');
    try {
      if (documentId) {
        await put(`/api/vehicle-documents/${documentId}/file`, body);
        toast.success('RC document replaced and submitted for verification.');
      } else {
        await post(`/api/vehicle-documents/vehicle/${vehicle.id}`, body);
        toast.success('RC document uploaded.');
      }
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
      setReplaceId(null);
      if (uploadRef.current) uploadRef.current.value = '';
      if (replaceRef.current) replaceRef.current.value = '';
    }
  };

  const download = async (document) => {
    try {
      setError('');
      const response = await api.get(`/api/vehicle-documents/${document.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = document.originalName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message);
    }
  };

  const remove = async (document) => {
    if (!window.confirm(`Delete ${document.originalName}?`)) return;
    setBusy(true);
    try {
      await deleteDocument(`/api/vehicle-documents/${document.id}`);
      toast.success('RC document deleted.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card border-0 bg-light mb-3">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <span className="vehicle-panel-kicker">Vehicle documents</span>
            <h4 className="h5 mb-1">RC book for {vehicle.vehicleNo}</h4>
            <p className="text-muted small mb-0">
              You can view and download your document at every verification stage.
            </p>
          </div>
          <button type="button" className="btn-close" aria-label="Close document panel" onClick={onClose} />
        </div>

        {error && <Alert type="danger" message={error} onClose={() => setError('')} />}

        {documents.map((document) => {
          const status = statusDetails[document.verificationStatus] || statusDetails.PENDING;
          return (
            <div className="bg-white border rounded-3 p-3 mb-2" key={document.id}>
              <div className="d-flex flex-wrap justify-content-between gap-3">
                <div className="d-flex gap-3">
                  <span className="rounded-3 bg-primary-subtle text-primary p-3 align-self-start"><FaFileAlt /></span>
                  <div>
                    <strong className="d-block">{document.originalName}</strong>
                    <span className={`badge ${status.className} my-1`}>{status.label}</span>
                    <small className="d-block text-muted">
                      Uploaded {new Date(document.createdAt).toLocaleString()}
                    </small>
                    {document.verificationComment && (
                      <small className="d-block mt-1">Administrator comment: {document.verificationComment}</small>
                    )}
                    {document.verifiedByName && (
                      <small className="d-block text-muted">
                        Reviewed by {document.verifiedByName} on {new Date(document.verifiedAt).toLocaleString()}
                      </small>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-wrap align-items-start gap-2">
                  <button className="btn btn-sm btn-outline-primary" onClick={() => download(document)} disabled={busy}>
                    <FaDownload className="me-1" /> Download
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setReplaceId(document.id);
                      setTimeout(() => replaceRef.current?.click(), 0);
                    }}
                    disabled={busy}
                  >
                    <FaSyncAlt className="me-1" /> Replace
                  </button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => remove(document)} disabled={busy}>
                    <FaTrash className="me-1" /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {documents.length === 0 && (
          <div className="text-center border border-dashed rounded-3 bg-white p-4 mb-3">
            <FaFileAlt className="text-muted mb-2" size={24} />
            <p className="mb-0">No RC document has been uploaded for this vehicle.</p>
          </div>
        )}

        <label className={`btn btn-sm btn-primary ${busy ? 'disabled' : ''}`}>
          <FaFileAlt className="me-1" /> {documents.length ? 'Upload another document' : 'Upload RC document'}
          <input
            ref={uploadRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            hidden
            disabled={busy}
            onChange={(event) => sendFile(event.target.files?.[0])}
          />
        </label>
        <input
          ref={replaceRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          hidden
          onChange={(event) => sendFile(event.target.files?.[0], replaceId)}
        />
        <small className="d-block text-muted mt-2">PDF, JPG or PNG. Replacing a verified document starts verification again.</small>
      </div>
    </div>
  );
}

VehicleDocuments.propTypes = {
  vehicle: PropTypes.shape({ id: PropTypes.number.isRequired, vehicleNo: PropTypes.string.isRequired }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default VehicleDocuments;
